/**
 * Universal file text extraction utility.
 * Extracts raw text from DOCX, TXT, and similar text-based files client-side.
 * Images and PDFs are handled via base64 → vision model on the backend.
 */
import mammoth from 'mammoth';

export type ExtractionStrategy = 'vision' | 'text' | 'unsupported';

/**
 * Determine how a file should be processed for AI extraction.
 */
export function getExtractionStrategy(mimeType: string): ExtractionStrategy {
  // Vision-capable: images and PDFs go through the vision model
  if (mimeType.startsWith('image/') || mimeType === 'application/pdf') {
    return 'vision';
  }

  // Text-extractable client-side
  const textMimes = new Set([
    'text/plain',
    'text/csv',
    'text/rtf',
    'application/rtf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  ]);
  if (textMimes.has(mimeType)) {
    return 'text';
  }

  return 'unsupported';
}

/**
 * Extract raw text from a text-based file (DOCX, TXT, CSV, RTF).
 * Returns the extracted string or throws on failure.
 */
export async function extractTextFromFile(file: File, mimeType: string): Promise<string> {
  // Plain text variants
  if (
    mimeType === 'text/plain' ||
    mimeType === 'text/csv' ||
    mimeType === 'text/rtf' ||
    mimeType === 'application/rtf'
  ) {
    return await file.text();
  }

  // DOCX via mammoth
  if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  }

  throw new Error(`Cannot extract text from MIME type: ${mimeType}`);
}

/**
 * Convert a File to a base64 string (without the data URI prefix).
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Infer MIME type from file, falling back to extension-based detection.
 */
export function inferMimeType(file: File): string {
  const detectedMime = file.type?.toLowerCase();
  if (detectedMime) return detectedMime;

  const extension = file.name.split('.').pop()?.toLowerCase();
  const byExtension: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    webp: 'image/webp',
    gif: 'image/gif',
    bmp: 'image/bmp',
    tif: 'image/tiff',
    tiff: 'image/tiff',
    heic: 'image/heic',
    heif: 'image/heif',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    pages: 'application/x-iwork-pages-sffpages',
    txt: 'text/plain',
    rtf: 'application/rtf',
    csv: 'text/csv',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  };

  return extension ? (byExtension[extension] || 'application/octet-stream') : 'application/octet-stream';
}

/**
 * Get a user-friendly message for unsupported file types.
 */
export function getUnsupportedFileMessage(file: File, mimeType: string): string {
  const extension = file.name.split('.').pop()?.toLowerCase() || '';

  if (extension === 'pages' || mimeType.includes('iwork')) {
    return 'Apple Pages files cannot be auto-extracted. Export as PDF or DOCX, then upload again.';
  }
  if (extension === 'doc' || mimeType === 'application/msword') {
    return 'Legacy .doc files are not supported. Please save as .docx or export as PDF, then upload again.';
  }
  if (extension === 'xls' || extension === 'xlsx') {
    return 'Excel files are not supported for patient extraction. Export as CSV or PDF, then upload again.';
  }

  return 'This file type cannot be auto-extracted. Convert to PDF, DOCX, or TXT and re-upload.';
}
